import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete as DeleteIcon,
  CloudUpload,
  Link as LinkIcon,
  Visibility,
  ReceiptLong,
  Inventory2
} from '@mui/icons-material';
import { format } from 'date-fns';
import { shipmentsInService } from '../services/shipmentsInService';
import { attachmentService } from '../services/attachmentService';
import { vendorService } from '../services/vendorService';
import {
  ProcurementSummaryBar,
  ProcurementSummaryField,
  formatShipmentPoDisplay,
  formatLinkedPoDisplay
} from '../components/procurement/ProcurementSummaryBar';
import { formatDateOnly, formatLocalTodayYmd } from '../utils/dateUtils';

const emptyReceiverForm = () => ({
  receiver_no: '',
  receiver_date: formatLocalTodayYmd(),
  vendor_id: null,
  name: '',
  po_number: '',
  purchase_order_id: null,
  invoice_id: null,
  billed: false,
  pack_list: '',
  vendor_type: '',
  ship_via: '',
  notes: ''
});

const lineItemToPayload = (it) => ({
  purchase_order_item_id: it.purchase_order_item_id ?? null,
  line_number: it.line_number ?? 0,
  part_id: it.part_id ?? null,
  part_number: it.part_number ?? null,
  description: it.description ?? null,
  quantity_ordered: Number(it.quantity_ordered) || 0,
  quantity_received: Number(it.quantity_received) || 0,
  unit: it.unit || 'EA',
  unit_price: Number(it.unit_price) || 0,
  line_total: Number(it.line_total) || ((Number(it.quantity_ordered) || 0) * (Number(it.unit_price) || 0)),
  notes: it.notes ?? null
});

const ProcurementReceivingDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const poQuery = searchParams.get('po') || '';
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [receiver, setReceiver] = useState(null);
  const [receiverForm, setReceiverForm] = useState(emptyReceiverForm());
  const [vendors, setVendors] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [stubMessage, setStubMessage] = useState('');

  const loadAttachments = async (receiverId) => {
    if (!receiverId) return setAttachments([]);
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForReceiver(receiverId);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const loadDetail = async () => {
    if (isNew) return;
    try {
      setLoading(true);
      setError('');
      const res = await shipmentsInService.getById(id);
      const data = res?.data;
      setReceiver(data);
      setReceiverForm({
        receiver_no: data?.receiver_no ?? '',
        receiver_date: (data?.receiver_date || '').toString().slice(0, 10) || formatLocalTodayYmd(),
        vendor_id: data?.vendor_id ?? null,
        name: data?.name ?? '',
        po_number: data?.po_number ?? '',
        purchase_order_id: data?.purchase_order_id ?? null,
        invoice_id: data?.invoice_id ?? null,
        billed: !!data?.billed,
        pack_list: data?.pack_list ?? '',
        vendor_type: data?.vendor_type ?? '',
        ship_via: data?.ship_via ?? '',
        notes: data?.notes ?? ''
      });
      setLineItems((data?.items || []).map((it, idx) => ({
        ...it,
        line_number: it.line_number ?? idx + 1,
        quantity_received: it.quantity_received ?? 0
      })));
      await loadAttachments(id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load shipment');
    } finally {
      setLoading(false);
    }
  };

  const prefillFromPo = async (poNumber) => {
    if (!poNumber) return;
    try {
      const res = await shipmentsInService.getPrefillFromPo(poNumber);
      const { header, items } = res?.data ?? {};
      if (header) {
        setReceiverForm((f) => ({
          ...f,
          vendor_id: header.vendor_id ?? f.vendor_id,
          name: header.name ?? f.name,
          po_number: header.po_number ?? poNumber,
          purchase_order_id: header.purchase_order_id ?? f.purchase_order_id,
          ship_via: header.ship_via ?? f.ship_via,
          notes: header.notes ?? f.notes
        }));
      }
      if (Array.isArray(items)) {
        setLineItems(items.map((it, idx) => ({ ...it, line_number: it.line_number ?? idx + 1, quantity_received: it.quantity_received ?? 0 })));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load from PO');
    }
  };

  useEffect(() => {
    if (isNew) {
      shipmentsInService.getNextNo()
        .then((res) => setReceiverForm((f) => ({ ...f, receiver_no: res?.data?.receiver_no ?? f.receiver_no })))
        .catch(() => {});
      if (poQuery) prefillFromPo(poQuery);
    } else {
      loadDetail();
    }
  }, [id, poQuery]);

  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const res = await vendorService.getAll({ limit: 500 });
        setVendors(res?.data ?? []);
      } catch {
        setVendors([]);
      }
    };
    loadVendors();
  }, []);

  const handleSave = async () => {
    if (!receiverForm.receiver_no?.trim()) return setError('Shipment number is required.');
    if (!receiverForm.name?.trim()) return setError('Received From is required.');
    try {
      setSaving(true);
      setError('');
      const payload = {
        ...receiverForm,
        items: lineItems.map(lineItemToPayload)
      };
      if (isNew) {
        const res = await shipmentsInService.create(payload);
        const newId = res?.data?.id ?? res?.id;
        if (newId) navigate(`/procurement/shipments-in/${newId}`);
        else navigate('/procurement/shipments-in');
      } else {
        await shipmentsInService.update(id, payload);
        await loadDetail();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save shipment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) return navigate('/procurement/shipments-in');
    try {
      setSaving(true);
      await shipmentsInService.delete(id);
      navigate('/procurement/shipments-in');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete receiver');
    } finally {
      setSaving(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleReceiveIntoStockStub = () => {
    setStubMessage('Not yet implemented. Stub for inventory updating.');
  };

  const formatDate = (v) => formatDateOnly(v, 'MM/dd/yyyy') || '—';

  const uploadAttachments = async (files) => {
    if (isNew || !id || !files?.length) return;
    if (files.length > 5) return setAttachmentsError('You can upload a maximum of 5 files at a time.');
    try {
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, receiverId: id });
      }
      await loadAttachments(id);
    } catch (err) {
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          {isNew ? 'New Shipment Received' : `Shipment Received ${receiver?.identifier ?? receiver?.receiver_no ?? ''}`.trim()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isNew && (
            <Tooltip title="Not yet implemented. Stub for inventory updating.">
              <span>
                <Button variant="outlined" color="primary" size="small" startIcon={<Inventory2 />} onClick={handleReceiveIntoStockStub}>
                  Receive into stock
                </Button>
              </span>
            </Tooltip>
          )}
          <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={() => navigate('/procurement/shipments-in')}>Back</Button>
          {!isNew && <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirmOpen(true)}>Delete</Button>}
          <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      {stubMessage && <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setStubMessage('')}>{stubMessage}</Alert>}

      {!isNew && receiver && (
        <ProcurementSummaryBar>
          <ProcurementSummaryField label="Shipment">
            <Box sx={{ fontWeight: 600 }}>{receiver.identifier ?? receiver.receiver_no ?? '—'}</Box>
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Date">{formatDate(receiver.receiver_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Vendor / received from">{receiver.name ?? '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="P.O.">{formatLinkedPoDisplay(receiver)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Cust. P.O.">{formatShipmentPoDisplay(receiver)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Billed">{receiver.billed ? 'Yes' : 'No'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Ship via">{receiver.ship_via ?? '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Notes" wide>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{receiver.notes ?? '—'}</Box>
          </ProcurementSummaryField>
        </ProcurementSummaryBar>
      )}

      {!isNew && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Flow Links (PO → Shipment Received → Vendor Invoice)</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" startIcon={<LinkIcon />} variant="outlined" onClick={() => navigate(`/procurement/po/${receiver?.purchase_order_id}`)} disabled={!receiver?.purchase_order_id}>
              View P.O.
            </Button>
            <Button size="small" startIcon={<ReceiptLong />} variant="outlined" onClick={() => navigate(`/procurement/invoices?po=${receiver?.purchase_order_id}&openNew=1`)}>
              Create vendor invoice
            </Button>
            <Button size="small" startIcon={<Visibility />} variant="outlined" onClick={() => navigate(`/procurement/invoices?po=${receiver?.purchase_order_id}`)}>
              View vendor invoices
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Shipment" value={receiverForm.receiver_no} onChange={(e) => setReceiverForm((f) => ({ ...f, receiver_no: e.target.value }))} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Date" type="date" value={receiverForm.receiver_date} onChange={(e) => setReceiverForm((f) => ({ ...f, receiver_date: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={12}>
            <FormControl size="small" sx={{ width: '20%' }}>
              <InputLabel id="shipment-received-from-label">Received From</InputLabel>
              <Select
                labelId="shipment-received-from-label"
                label="Received From"
                value={receiverForm.vendor_id ?? ''}
                onChange={(e) => {
                  const vendorId = e.target.value === '' ? null : Number(e.target.value);
                  const vendor = vendors.find((v) => Number(v.id) === Number(vendorId));
                  setReceiverForm((f) => ({
                    ...f,
                    vendor_id: vendorId,
                    name: vendor?.name ?? ''
                  }));
                }}
              >
                <MenuItem value="">
                  <em>Select vendor</em>
                </MenuItem>
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {!isNew && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Received From (manual override)"
                value={receiverForm.name}
                onChange={(e) => setReceiverForm((f) => ({ ...f, name: e.target.value }))}
                helperText="Optional manual override; normally sourced from selected vendor."
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Cust. P.O."
              value={receiverForm.po_number}
              onChange={(e) => setReceiverForm((f) => ({ ...f, po_number: e.target.value }))}
              helperText={receiverForm.purchase_order_id ? 'Linked to purchase order' : 'Without a linked PO, saves as UNKNOWN PO when blank.'}
            />
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Pack List" value={receiverForm.pack_list} onChange={(e) => setReceiverForm((f) => ({ ...f, pack_list: e.target.value }))} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Vendor Type" value={receiverForm.vendor_type} onChange={(e) => setReceiverForm((f) => ({ ...f, vendor_type: e.target.value }))} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Ship Via" value={receiverForm.ship_via} onChange={(e) => setReceiverForm((f) => ({ ...f, ship_via: e.target.value }))} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Notes" multiline rows={2} value={receiverForm.notes} onChange={(e) => setReceiverForm((f) => ({ ...f, notes: e.target.value }))} /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Line Items</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="caption">Part / Description</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Qty ordered</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Qty received</Typography></TableCell>
                <TableCell><Typography variant="caption">Unit</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Unit price</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary">No line items</Typography></TableCell></TableRow>
              ) : lineItems.map((it, idx) => (
                <TableRow key={idx}>
                  <TableCell>{it.part_number || ''} {it.description ? ` - ${it.description}` : ''}</TableCell>
                  <TableCell align="right">{Number(it.quantity_ordered) || 0}</TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" inputProps={{ min: 0 }} value={it.quantity_received ?? ''} onChange={(e) => setLineItems((prev) => prev.map((x, i) => i === idx ? { ...x, quantity_received: e.target.value } : x))} sx={{ width: 90 }} />
                  </TableCell>
                  <TableCell>{it.unit || 'EA'}</TableCell>
                  <TableCell align="right">{Number(it.unit_price || 0).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!isNew && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Box
            onDrop={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDraggingOverAttachment(false);
              const files = e.dataTransfer?.files;
              if (files?.length) await uploadAttachments(Array.from(files));
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingOverAttachment(true); }}
            onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingOverAttachment(false); }}
            sx={{
              p: 2, mb: 1, minHeight: 120, border: '2px dashed',
              borderColor: isDraggingOverAttachment ? 'primary.main' : 'grey.400',
              borderRadius: 2, bgcolor: isDraggingOverAttachment ? 'action.hover' : 'grey.50',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Drop documents for Shipment Received {receiver?.identifier ?? receiver?.receiver_no}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>or use Upload · max 5 files, 10 MB each</Typography>
            </Box>
            <Button variant="contained" component="label" size="small" startIcon={<CloudUpload />}>
              Upload
              <input type="file" hidden multiple onChange={async (e) => { const files = e.target.files; if (files?.length) await uploadAttachments(Array.from(files)); e.target.value = ''; }} />
            </Button>
          </Box>
          {attachmentsError && <Alert severity="error" sx={{ mb: 1 }}>{attachmentsError}</Alert>}
          {attachmentsLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={24} /></Box> : (
            attachments.length === 0 ? <Typography variant="body2" color="text.secondary">No attachments yet.</Typography> : (
              <TableContainer><Table size="small"><TableHead><TableRow><TableCell>File Name</TableCell><TableCell>Size</TableCell><TableCell>Uploaded At</TableCell><TableCell width={80}>Actions</TableCell></TableRow></TableHead><TableBody>
                {attachments.map((att) => (
                  <TableRow key={att.id} hover>
                    <TableCell><Button size="small" sx={{ textTransform: 'none' }} onClick={async () => {
                      try {
                        const res = await attachmentService.download(att.id);
                        const blob = res.data;
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = att.file_name || `attachment-${att.id}`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
                      }
                    }}>{att.file_name}</Button></TableCell>
                    <TableCell>{att.file_size != null ? `${(att.file_size / 1024 / 1024).toFixed(2)} MB` : '—'}</TableCell>
                    <TableCell>{att.uploaded_at ? format(new Date(att.uploaded_at), 'MM/dd/yyyy HH:mm') : '—'}</TableCell>
                    <TableCell><IconButton size="small" color="error" onClick={async () => {
                      try {
                        await attachmentService.delete(att.id);
                        setAttachments((prev) => prev.filter((a) => a.id !== att.id));
                      } catch (err) {
                        setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
                      }
                    }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody></Table></TableContainer>
            )
          )}
        </Paper>
      )}

      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
      <DialogTitle>Delete Shipment Received?</DialogTitle>
        <DialogContent>Delete <strong>{receiver?.identifier ?? receiver?.receiver_no ?? id}</strong>? This cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcurementReceivingDetail;

